import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios, { getApiUrl } from "@/lib/api";
import { MessageSquare, Flag, ShieldX, CornerDownRight, AtSign } from "lucide-react";

const API = `${getApiUrl()}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch {
    return "";
  }
}

const ICONS = {
  reply: CornerDownRight,
  mention: AtSign,
  admin_removed: ShieldX,
  report: Flag,
};

export default function CommentActivityBell({ user }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchInbox = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/community/comment-notifications/inbox`);
      setNotes(data?.notifications || []);
      setUnread(data?.unreadCount || 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchInbox();
    const interval = setInterval(fetchInbox, 60000);
    return () => clearInterval(interval);
  }, [fetchInbox]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      try {
        await axios.post(`${API}/community/comment-notifications/mark-read`);
        setUnread(0);
      } catch {
        /* silent */
      }
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={toggle}
        className="p-2 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors relative"
        title="Community activity"
        data-testid="comment-activity-bell"
      >
        <MessageSquare className={`w-5 h-5 ${unread > 0 ? "text-purple-400" : ""}`} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
            data-testid="comment-activity-unread">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-[calc(100vw-2rem)] sm:w-80 max-w-80 bg-[#0A0A0A] border border-white/10 rounded-lg shadow-xl z-50 max-h-96 overflow-hidden flex flex-col"
          data-testid="comment-activity-dropdown">
          <div className="p-3 border-b border-white/10">
            <span className="font-medium text-white text-sm">Community Activity</span>
          </div>
          <div className="overflow-y-auto">
            {notes.length === 0 ? (
              <p className="text-white/30 text-xs italic p-4 text-center">No activity yet.</p>
            ) : (
              notes.map((n) => {
                const Icon = ICONS[n.type] || MessageSquare;
                return (
                  <button
                    key={n.id}
                    onClick={() => {
                      setOpen(false);
                      navigate("/social");
                    }}
                    className="w-full text-left flex items-start gap-2.5 p-3 hover:bg-white/5 border-b border-white/5 transition-colors"
                    data-testid={`comment-activity-item-${n.id}`}
                  >
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${n.type === "admin_removed" ? "text-red-400" : n.type === "report" ? "text-orange-400" : "text-purple-400"}`} />
                    <div className="min-w-0">
                      <p className="text-xs text-white/80 break-words">{n.message}</p>
                      <p className="text-[10px] text-white/30 mt-0.5">{timeAgo(n.createdAt)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
